import React, { useState, useEffect } from 'react';
import { Header, Footer } from '@/components/layout';
import { HeroCarousel, type CarouselSlide } from '@/components/features/HeroCarousel';
import { BecomeAFriend } from '@/components/features/BecomeAFriend';
import { Button } from '@/kenya_friars_components/buttons';
import { Link } from '@/kenya_friars_components/links';
import { FaClock, FaMapMarkerAlt, FaHandsHelping } from 'react-icons/fa';
import { DonationModal } from '../Donation/DonationModal';
import './Home.css';

// Import carousel images
import subukiaImg from '@/assets/images/corousel/Subukia-shrine-1.jpg';
import stAnthonyHomeImg from '@/assets/images/corousel/st_antony_childrens_home.jpeg';
import waterProjectImg from '@/assets/images/corousel/water_project.jpg';
import healthCenterImg from '@/assets/images/corousel/ruiri-catholic-mission-health-center.jpg';
import schoolMeruImg from '@/assets/images/corousel/St.-Anthony-primary-school-Ruiri.jpg';
import vocationsImg from '@/assets/images/corousel/vocations.jpg';
import homeMissionImg from '@/assets/images/home_mission.jpeg';
import homeLetterImg from '@/assets/images/home_letter.jpeg';
import rwandaMissionImg from '@/assets/images/rwanda-mission.jpeg';
import eventsPlaceholderImg from '@/assets/images/Subukia-shrine-1.jpg';
import ministerAvatarImg from '@/assets/images/st-francis-of-assisi.png';
import videoThumbnailImg from '@/assets/images/worshipers.jpeg';

const carouselSlides: CarouselSlide[] = [
  {
    id: '1',
    subtitle: 'DIVINE CALLING',
    title: 'Start Your Journey Today',
    description: 'Are you called to live the Franciscan charism of fraternity, prayer, and service? Explore our vocational path.',
    image: vocationsImg,
    link: '/vocations',
    buttonText: 'Discover Vocations',
  },
  {
    id: '2',
    subtitle: 'SPIRITUAL PILGRIMAGE',
    title: 'Subukia National Shrine',
    description: 'Experience deep peace and spiritual restoration at the Village of Mary Mother of God, the National Marian Shrine of Kenya.',
    image: subukiaImg,
    link: '/subukia',
    buttonText: 'Plan Your Visit',
  },
  {
    id: '3',
    subtitle: 'COMPASSIONATE CARE',
    title: "St. Anthony Children's Home",
    description: 'Providing a sanctuary of love, education, and hope for orphaned and vulnerable children in Limuru.',
    image: stAnthonyHomeImg,
    link: '/apostolates/charitable',
    buttonText: 'Support Our Children',
  },
  {
    id: '4',
    subtitle: 'TRANSFORMING LIVES',
    title: 'Water Projects & Community Aid',
    description: 'Bringing clean water and essential resources to underserved communities, living the Franciscan mission of service.',
    image: waterProjectImg,
    link: '/apostolates/charitable',
    buttonText: 'See Our Projects',
  },
  {
    id: '5',
    subtitle: 'HEALING MISSION',
    title: 'Ruiri Mission Health Centre',
    description: 'Dedicated to providing quality healthcare and maternal services to the community with dignity and grace.',
    image: healthCenterImg,
    link: '/apostolates/healthcare',
    buttonText: 'Learn About Healthcare',
  },
  {
    id: '6',
    subtitle: 'EMPOWERING MINDS',
    title: 'St. Anthony Primary School',
    description: 'Nurturing the future generation through academic excellence and spiritual formation in Meru County.',
    image: schoolMeruImg,
    link: '/apostolates/education',
    buttonText: 'Our Schools',
  },
];

import { API_BASE, UPLOADS_BASE } from '@/services/api';

const EVENTS_PLACEHOLDER_IMAGE = eventsPlaceholderImg;

interface ApiEvent {
  id: number;
  title: string;
  short_description: string;
  host: string;
  start_date: string;
  start_time: string;
  end_date: string | null;
  end_time: string | null;
  location: string;
  feature_image: string | null;
  status: string;
}

function getUploadUrl(path: string | null): string {
  if (!path) return '';
  return path.startsWith('http') ? path : UPLOADS_BASE + path.replace(/^\//, '');
}

function formatTime(t: string | null): string {
  if (!t) return '';
  const s = t.slice(0, 5);
  if (!s) return '';
  const [hh, mm] = s.split(':').map(Number);
  const h = hh % 12 || 12;
  const ampm = hh < 12 ? 'AM' : 'PM';
  return `${h}:${String(mm).padStart(2, '0')} ${ampm}`;
}

function formatEventForCard(e: ApiEvent): {
  id: number;
  title: string;
  date: { day: string; month: string };
  time: string;
  location: string;
  category: string;
  description: string;
  image: string;
} {
  const d = e.start_date ? new Date(e.start_date) : new Date();
  const day = String(d.getDate()).padStart(2, '0');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[d.getMonth()];
  const start = formatTime(e.start_time);
  const end = e.end_time ? formatTime(e.end_time) : '';
  const timeStr = end ? `${start} – ${end}` : start;
  const image = (e.feature_image && String(e.feature_image).trim()) ? getUploadUrl(e.feature_image) : EVENTS_PLACEHOLDER_IMAGE;
  const category = e.status ? String(e.status).toUpperCase() : 'EVENT';
  return {
    id: e.id,
    title: e.title,
    date: { day, month },
    time: timeStr,
    location: e.location,
    category,
    description: e.short_description || '',
    image,
  };
}

export const Home: React.FC = () => {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const [events, setEvents] = useState<ApiEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [isDonationModalOpen, setIsDonationModalOpen] = useState(false);
  const [selectedCause, setSelectedCause] = useState<{
    title: string;
    description: string;
    icon?: React.ReactNode;
  } | null>(null);

  const toggleFaq = (index: number) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setEventsLoading(true);
    setEventsError(null);
    const url = `${API_BASE}events/list&status=upcoming&limit=6`;
    fetch(url)
      .then((res) => res.json())
      .then((json) => {
        if (cancelled) return;
        if (!json.success || !Array.isArray(json.data)) {
          setEventsError(json?.message || 'Failed to load events');
          setEvents([]);
          return;
        }
        const list = json.data as ApiEvent[];
        const sorted = [...list].sort((a, b) => {
          const da = `${a.start_date} ${a.start_time || ''}`;
          const db = `${b.start_date} ${b.start_time || ''}`;
          return da.localeCompare(db);
        });
        setEvents(sorted.slice(0, 3));
      })
      .catch((err) => {
        if (!cancelled) {
          setEventsError(err instanceof Error ? err.message : 'Failed to load events');
          setEvents([]);
        }
      })
      .finally(() => {
        if (!cancelled) setEventsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="kf-home kf-page">
      <Header />

      <HeroCarousel slides={carouselSlides} interval={12000} />

      <section className="kf-home__cta-section">
        <div className="container">
          <div className="kf-home__cta-content">
            <div className="kf-home__cta-text">
              <h3 className="kf-home__cta-title">
                Join the Conventual Franciscan Friars in
              </h3>
              <p className="kf-home__cta-subtitle">
                Promoting Justice, Peace, and Integrity of Creation
              </p>
            </div>
            <div className="kf-home__cta-button-wrapper">
              <Button variant="outline-white" href="/vocations" size="lg">
                JOIN OUR MISSION
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="kf-home__mission-vision">
        <div className="container">
          <div className="kf-home__mission-grid">
            <div className="kf-home__mission-visual">
              <div className="kf-home__mission-image-card">
                <img
                  src={homeMissionImg}
                  alt="Kenya Friars Mission"
                  className="kf-home__mission-img"
                />
                <div className="kf-home__mission-exp">EST. 1984</div>
              </div>
              <div className="kf-home__mission-floating-accent"></div>
            </div>
            <div className="kf-home__mission-text-content">
              <div className="kf-home__badge">OUR MISSION & VISION</div>
              <div className="kf-home__mission-blocks">
                <div className="kf-home__info-block">
                  <h2 className="kf-home__info-title">MISSION</h2>
                  <p className="kf-home__info-text">
                    Our mission, like the mission of St. Francis of Assisi himself, is to imitate
                    Christ in all that he said and did as recorded in the Gospels and taught by
                    church tradition. We strive to be instruments of peace, bringing the love of Christ to all people,
                    especially those who are poor, marginalized, and in need of God's mercy and compassion.
                  </p>
                </div>
                <div className="kf-home__info-block">
                  <h2 className="kf-home__info-title">VISION</h2>
                  <p className="kf-home__info-text">
                    Animate our communal life from which we draw the strength to share with our brothers and sisters
                    the joy of Christ by doing penance, preaching and the witness of life. As pilgrims in this world,
                    we seek to be a living witness to the Gospel, building communities of faith, hope, and love.
                  </p>
                </div>
              </div>
              <Link to="/about">
                <Button variant="outline" size="lg">DISCOVER OUR HISTORY</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="kf-home__letter-section">
        <div className="kf-home__letter-content">
          <div className="kf-home__letter-image-wrapper">
            <img
              src={homeLetterImg}
              alt="Message from the Minister Provincial"
              className="kf-home__letter-image"
            />
          </div>
          <div className="kf-home__letter-text-box">
            <div className="kf-home__letter-text-inner">
              <div className="kf-home__letter-header">
                <span className="kf-home__letter-badge">OFMCONV</span>
                <h2 className="kf-home__letter-title">
                  LETTER FOR THE FRIENDS OF THE CONVENTUAL FRANCISCAN FRIARS
                </h2>
              </div>
              <div className="kf-home__letter-body">
                <p className="kf-home__letter-greeting">Dear Friends,</p>
                <div className="kf-home__letter-message">
                  <p>
                    <strong>Resurrection Day!</strong>
                  </p>
                  <p>
                    Resurrection Day brings hope and love, reminding us of Christ's sacrifice and
                    glorious victory over death. His selfless offering warms cold hearts and
                    restores relationships. Even those far away gather to rejoice in the hymn,
                    Christ is Risen!. Let us celebrate with love, forgiveness, and unity as one
                    family in the Risen Lord.
                  </p>
                </div>
              </div>
              <div className="kf-home__letter-signature">
                <div className="kf-home__letter-avatar">
                  <img
                    src={ministerAvatarImg}
                    alt="Friar Hillary Raduk"
                  />
                </div>
                <div className="kf-home__letter-signature-info">
                  <p className="kf-home__letter-author">Friar Hillary Raduk</p>
                  <p className="kf-home__letter-role">Minister Provincial</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="kf-home__videos-section">
        <div className="container">
          <div className="kf-home__videos-wrapper">
            <div className="kf-home__videos-content">
              <div className="kf-home__videos-badge">WATCH & LEARN</div>
              <h2 className="kf-home__videos-title">Conventuals Videos</h2>
              <p className="kf-home__videos-description">
                Explore our collection of inspiring videos showcasing the mission, work, and
                spiritual journey of the Conventual Franciscan Friars in Kenya.
              </p>
              <a
                href="https://www.youtube.com/@ConventualFranciscanFriars-Ken/videos"
                target="_blank"
                rel="noopener noreferrer"
                className="kf-home__video-cta"
              >
                <Button variant="gold" size="lg">
                  WATCH ON YOUTUBE
                </Button>
              </a>
            </div>
            <div className="kf-home__videos-preview">
              <a
                href="https://www.youtube.com/watch?v=uMEMgb_4E1Q"
                target="_blank"
                rel="noopener noreferrer"
                className="kf-home__videos-thumbnail-link"
              >
                <div className="kf-home__videos-thumbnail">
                  <div className="kf-home__videos-thumbnail-bg">
                    <img
                      src={videoThumbnailImg}
                      alt="Conventuals Video"
                      className="kf-home__videos-thumbnail-image"
                    />
                  </div>
                  <div className="kf-home__videos-duration">12:45</div>
                  <div className="kf-home__videos-progress">
                    <div className="kf-home__videos-progress-bar" style={{ width: '45%' }}></div>
                  </div>
                  <div className="kf-home__videos-play-overlay">
                    <svg
                      className="kf-home__videos-play-icon"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                  <div className="kf-home__videos-youtube-badge">
                    <svg
                      className="kf-home__videos-youtube-icon"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                    </svg>
                    <span>YouTube</span>
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="kf-home__events-section">
        <div className="container">
          <div className="kf-home__events-header-container">
            <div className="kf-home__events-header-left">
              <span className="kf-home__events-badge">CALENDAR OF EVENTS</span>
              <h2 className="kf-home__events-title">Upcoming Events</h2>
            </div>
            <div className="kf-home__events-header-right">
              <p className="kf-home__events-header-desc">
                Join our Franciscan family in prayer, celebration, and service.
                Explore our upcoming events across various friaries and missions.
              </p>
              <Link to="/events">
                <Button variant="outline" size="sm">VIEW ALL EVENTS</Button>
              </Link>
            </div>
          </div>

          <div className="kf-home__events-grid">
            {eventsLoading ? (
              <p className="kf-home__events-loading">Loading upcoming events…</p>
            ) : eventsError ? (
              <p className="kf-home__events-error" role="alert">{eventsError}</p>
            ) : events.length === 0 ? (
              <p className="kf-home__events-empty">No upcoming events at the moment. Check back soon or view our full calendar.</p>
            ) : (
              events.map((event) => {
                const card = formatEventForCard(event);
                return (
                  <article key={card.id} className="kf-home__event-card">
                    <div className="kf-home__event-image-wrapper">
                      <img
                        src={card.image}
                        alt={card.title}
                        className="kf-home__event-image"
                        onError={(e) => {
                          const el = e.currentTarget;
                          if (el.src !== EVENTS_PLACEHOLDER_IMAGE) el.src = EVENTS_PLACEHOLDER_IMAGE;
                        }}
                      />
                      <div className="kf-home__event-image-overlay">
                        <h3 className="kf-home__event-card-title">{card.title}</h3>
                      </div>
                      <span className="kf-home__event-category">{card.category}</span>
                      <div className="kf-home__event-date-badge">
                        <span className="kf-home__event-date-day">{card.date.day}</span>
                        <span className="kf-home__event-date-month">{card.date.month}</span>
                      </div>
                    </div>
                    <div className="kf-home__event-content">
                      <div className="kf-home__event-details">
                        <div className="kf-home__event-detail-item">
                          <FaClock /> <span>{card.time}</span>
                        </div>
                        <div className="kf-home__event-detail-item">
                          <FaMapMarkerAlt /> <span>{card.location}</span>
                        </div>
                      </div>
                      <p className="kf-home__event-description">{card.description}</p>
                      <div className="kf-home__event-footer">
                        <Link to="/events" className="kf-home__event-link">
                          Learn More →
                        </Link>
                      </div>
                    </div>
                  </article>
                );
              })
            )}
          </div>
        </div>
      </section>

      <section className="kf-home__faq-section">
        <div className="container">
          <div className="kf-home__faq-header-wrapper">
            <span className="kf-home__faq-badge">ANY QUESTIONS?</span>
            <h1 className="kf-home__faq-title">Frequently Asked Questions</h1>
          </div>
          <div className="kf-home__faq-content">
            <div className={`kf-home__faq-item ${activeFaq === 0 ? 'is-active' : ''}`}>
              <button
                className="kf-home__faq-question-btn"
                onClick={() => toggleFaq(0)}
                aria-expanded={activeFaq === 0}
              >
                <h5 className="kf-home__faq-question">
                  Who are the Conventual Franciscan Friars
                </h5>
                <span className="kf-home__faq-icon"></span>
              </button>
              <div className="kf-home__faq-answer-wrapper">
                <div className="kf-home__faq-answer">
                  <p>
                    The Conventual Franciscan Friars are a Catholic religious order following the
                    teachings and example of St. Francis of Assisi. They live in community,
                    practice vows of poverty, chastity, and obedience, and focus on a life of
                    prayer, fraternity, and service to others. Their mission includes preaching
                    the Gospel, serving the poor, and promoting peace and justice.
                  </p>
                  <p>
                    Conventual Franciscans serve in many countries around the world. They are
                    involved in various ministries, including pastoral care in parishes,
                    education, social services, missionary work, and the promotion of peace and
                    justice. They also maintain shrines, retreat centers, and places of
                    pilgrimage.
                  </p>
                </div>
              </div>
            </div>

            <div className={`kf-home__faq-item ${activeFaq === 1 ? 'is-active' : ''}`}>
              <button
                className="kf-home__faq-question-btn"
                onClick={() => toggleFaq(1)}
                aria-expanded={activeFaq === 1}
              >
                <h5 className="kf-home__faq-question">
                  Is it possible to join The conventual Franciscan Friars
                </h5>
                <span className="kf-home__faq-icon"></span>
              </button>
              <div className="kf-home__faq-answer-wrapper">
                <div className="kf-home__faq-answer">
                  <p>
                    <strong>Yes, it is possible to join the Conventual Franciscan Friars.</strong> Here's what you need to know about the process:
                  </p>
                  <p>
                    <strong>1. Who can join the Conventual Franciscan Friars?</strong>
                  </p>
                  <p>
                    Men who feel called to live a life dedicated to prayer, community, and service according to the spirit of St. Francis of Assisi can join the Conventual Franciscans. Candidates must be Catholic, have a desire to serve the Church and others, and be open to the values of poverty, chastity, and obedience.
                  </p>
                  <p>
                    <strong>2. What is the process of joining the order?</strong>
                  </p>
                  <p>
                    Joining the Conventual Franciscan Friars involves several stages of formation:
                  </p>
                  <ul>
                    <li><strong>Discernment:</strong> This is an initial phase where a person prays and reflects on their calling. It often involves meetings with a vocation director or visiting Franciscan communities to better understand their way of life.</li>
                    <li><strong>Postulancy:</strong> This is the first formal stage where the individual lives in a Franciscan community for about a year, experiencing the daily life of the friars while deepening their understanding of the Franciscan charism.</li>
                    <li><strong>Novitiate:</strong> After postulancy, the individual enters the novitiate, a more intense year of prayer, study, and reflection. This year is dedicated to deepening one's relationship with God and understanding the Franciscan lifestyle.</li>
                    <li><strong>Temporary Vows:</strong> After the novitiate, the friar takes temporary vows of poverty, chastity, and obedience, which are renewed annually for a few years as the friar continues his formation.</li>
                    <li><strong>Perpetual Vows:</strong> After a period of temporary vows, if the friar is ready and has discerned his commitment, he takes perpetual (final) vows, committing to live as a Franciscan for life.</li>
                  </ul>
                  <p>
                    <strong>3. Do you have to be a priest to join?</strong>
                  </p>
                  <p>
                    No, you do not have to be a priest to join. The Conventual Franciscans welcome both men who feel called to the priesthood and those who wish to be brothers. All friars, whether priests or brothers, live together in community and contribute to the mission of the order.
                  </p>
                  <p>
                    <strong>4. Is there an age limit to join?</strong>
                  </p>
                  <p>
                    While there is often a preferred age range (typically 18-45), exceptions can be made depending on the circumstances. It's best to speak with a vocation director to understand the specific requirements of the province you are interested in.
                  </p>
                  <p>
                    <strong>5. How do I start the process?</strong>
                  </p>
                  <p>
                    If you are interested in joining, the first step is to reach out to a vocation director of the Conventual Franciscans. They can provide guidance, answer your questions, and help you discern if this path is right for you. You can find contact information on the official website of the Conventual Franciscan province in your region.
                  </p>
                  <p>
                    Joining the Conventual Franciscan Friars is a journey of discernment and commitment, and it is open to those who feel called to follow in the footsteps of St. Francis in a life of fraternity and service. Feel free to fill the vocation initial form <Link to="/vocations">here</Link> to get started with your journey.
                  </p>
                </div>
              </div>
            </div>

            <div className={`kf-home__faq-item ${activeFaq === 2 ? 'is-active' : ''}`}>
              <button
                className="kf-home__faq-question-btn"
                onClick={() => toggleFaq(2)}
                aria-expanded={activeFaq === 2}
              >
                <h5 className="kf-home__faq-question">
                  Are the Conventual Franciscan Friars Catholics
                </h5>
                <span className="kf-home__faq-icon"></span>
              </button>
              <div className="kf-home__faq-answer-wrapper">
                <div className="kf-home__faq-answer">
                  <p>
                    <strong>Yes, the Conventual Franciscan Friars are Catholic.</strong> They are part of the larger Franciscan family, which is a religious order within the Roman Catholic Church. Founded by St. Francis of Assisi in the 13th century, the Conventual Franciscan Friars adhere to Catholic teachings and traditions, and they dedicate their lives to serving the Church and its mission.
                  </p>
                  <p>
                    Their work includes celebrating the sacraments, preaching the Gospel, and engaging in various forms of ministry, such as education, pastoral care, and missionary efforts. As Catholics, they also uphold the Church's doctrines and participate in the liturgical life of the Church, including daily Mass and the Liturgy of the Hours.
                  </p>
                </div>
              </div>
            </div>

            <div className={`kf-home__faq-item ${activeFaq === 3 ? 'is-active' : ''}`}>
              <button
                className="kf-home__faq-question-btn"
                onClick={() => toggleFaq(3)}
                aria-expanded={activeFaq === 3}
              >
                <h5 className="kf-home__faq-question">
                  How can I support the work of the conventual Franciscan Friars
                </h5>
                <span className="kf-home__faq-icon"></span>
              </button>
              <div className="kf-home__faq-answer-wrapper">
                <div className="kf-home__faq-answer">
                  <p>
                    Supporting the work of the Conventual Franciscan Friars can be done in various ways, ranging from financial contributions to volunteering your time. Here are some ways you can support their mission:
                  </p>
                  <p>
                    <strong>1. Financial Donations</strong>
                  </p>
                  <ul>
                    <li><strong>Direct Donations:</strong> The Conventual Franciscan friars in Kenya allow donations on the website <Link to="/donation">(Kenya Friars)</Link> where you can make online donations. These funds often support their ministries, education, mission work, and the daily needs of the friars.</li>
                    <li><strong>Sponsorships:</strong> You can sponsor specific projects, such as the education of friars in formation, building maintenance, or supporting their missions in poorer regions.</li>
                    <li><strong>Legacy Giving:</strong> Consider including the Conventual Franciscans in your estate planning through bequests, trusts, or other planned gifts. This helps ensure their work continues for future generations.</li>
                  </ul>
                  <p>
                    <strong>2. Prayer Support</strong>
                  </p>
                  <ul>
                    <li><strong>Pray for the Friars:</strong> The friars value spiritual support just as much as financial support. Praying for their mission, vocations, and the people they serve is a powerful way to contribute to their work.</li>
                    <li><strong>Join in Novenas or Special Masses:</strong> Many Franciscan communities host novenas or special prayers that you can join, either in person or online.</li>
                  </ul>
                  <p>
                    <strong>3. Volunteering</strong>
                  </p>
                  <ul>
                    <li><strong>Help with Local Ministries:</strong> Many friaries run parishes, soup kitchens, retreat centers, and other outreach programs. Volunteering your time to assist in these activities can be a great way to contribute directly to their mission.</li>
                    <li><strong>Offer Professional Skills:</strong> If you have skills in areas like construction, IT, teaching, or healthcare, your expertise might be valuable in supporting their projects and initiatives.</li>
                  </ul>
                  <p>
                    <strong>4. Spread Awareness</strong>
                  </p>
                  <ul>
                    <li><strong>Share Their Mission:</strong> You can support the Conventual Franciscans by raising awareness about their work through social media or in your local community. This can help them reach more people who might want to support their mission.</li>
                    <li><strong>Organize Fundraisers:</strong> Hosting local events, fundraisers, or charity drives can help raise funds and resources for the friars' work. This can be a meaningful way to involve your community.</li>
                  </ul>
                  <p>
                    <strong>5. Purchase from Their Initiatives</strong>
                  </p>
                  <ul>
                    <li><strong>Support Their Publications or Crafts:</strong> Some friaries produce religious books, artwork, or handmade crafts. Purchasing these items is a great way to provide financial support while benefiting from the spiritual resources they offer.</li>
                    <li><strong>Attend Their Retreats or Workshops:</strong> Many Franciscan communities offer retreats, spiritual workshops, or pilgrimages. By participating, you not only enrich your spiritual life but also support the friars' ministry financially.</li>
                  </ul>
                  <p>
                    <strong>6. Encourage Vocations</strong>
                  </p>
                  <ul>
                    <li><strong>Promote Religious Life:</strong> Encourage young people who may feel called to explore a vocation with the Conventual Franciscans. Sharing information about vocations with friends, family, or members of your parish can be an invaluable way to support the growth of the community.</li>
                    <li><strong>Vocation Prayer Groups:</strong> Join or start a prayer group focused on praying for an increase in vocations to the Conventual Franciscan Order.</li>
                  </ul>
                  <p>
                    Supporting the Conventual Franciscan Friars helps them continue their mission of spreading the Gospel, serving those in need, and being a presence of peace and hope in the world. Whether through donations, prayer, or your time, every contribution is appreciated and makes a difference.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="kf-home__rwanda-section">
        <div className="container">
          <div className="kf-home__rwanda-grid">
            <div className="kf-home__rwanda-content">
              <span className="kf-home__badge">NEW FRONTIERS</span>
              <h2 className="kf-home__rwanda-title">Mission in Rwanda</h2>
              <div className="kf-home__rwanda-text-wrapper">
                <p className="kf-home__rwanda-text">
                  The Conventual Franciscan Friars have taken their first step in Rwanda at <strong>Nyagasambu Parish</strong>.
                  This historic expansion marks a new chapter in our mission of service and fraternity beyond borders.
                </p>
                <p className="kf-home__rwanda-text">
                  We are currently projecting to build a new mission there—a sanctuary of faith, education, and hope.
                  You are invited to be part of this extraordinary journey and help us plant the seeds of peace in Rwanda.
                  Every contribution helps bring this vision closer to reality.
                </p>
              </div>
              <div className="kf-home__rwanda-actions">
                <Button
                  variant="gold"
                  size="lg"
                  onClick={() => {
                    setSelectedCause({
                      title: 'Mission in Rwanda',
                      description: 'The Conventual Franciscan Friars have taken their first step in Rwanda at Nyagasambu Parish. They are projecting to build a new mission there, and you are invited to be part of this historic journey.',
                      icon: <FaHandsHelping />
                    });
                    setIsDonationModalOpen(true);
                  }}
                >
                  BE PART OF THE JOURNEY
                </Button>
              </div>
            </div>
            <div className="kf-home__rwanda-visual">
              <div className="kf-home__rwanda-image-card">
                <img
                  src={rwandaMissionImg}
                  alt="Mission in Rwanda"
                  className="kf-home__rwanda-img"
                />
                <div className="kf-home__rwanda-exp">NYAGASAMBU PARISH</div>
                <div className="kf-home__rwanda-floating-accent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="kf-home__subukia-section">
        <div
          className="kf-home__subukia-parallax"
          style={{ backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0.45)), url(${subukiaImg})` }}
        >
          <div className="container">
            <div className="kf-home__subukia-glass">
              <div className="kf-home__subukia-badge">NATIONAL SHRINE</div>
              <h2 className="kf-home__subukia-title">Village of Mary Mother of God</h2>
              <p className="kf-home__subukia-description">
                Experience the profound peace and spiritual restoration at the Subukia Shrine.
                A place where nature meets divinity and hearts find solace.
              </p>
              <div className="kf-home__subukia-actions">
                <Link to="/booking">
                  <Button variant="gold" size="lg">
                    PLAN YOUR PILGRIMAGE
                  </Button>
                </Link>
                <Link to="/subukia">
                  <Button variant="outline-white" size="lg">
                    LEARN MORE
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BecomeAFriend />

      <Footer />

      <DonationModal
        isOpen={isDonationModalOpen}
        onClose={() => {
          setIsDonationModalOpen(false);
          setSelectedCause(null);
        }}
        selectedCause={selectedCause || undefined}
      />
    </div>
  );
};
